import { useQuery } from '@tanstack/react-query';

import { tenantApi } from './api/tenant.api';
import type { CatalogKind } from './types';

export const tenantKeys = { info: ['tenant'] as const, members: ['tenant', 'members'] as const, invitations: ['tenant', 'invitations'] as const, roles: ['tenant', 'roles'] as const, permissions: ['tenant', 'permissions'] as const, jobTitles: ['tenant', 'job-titles'] as const, departments: ['tenant', 'departments'] as const, catalog: (kind: string) => ['tenant', 'catalog', kind] as const, workingDays: ['tenant', 'working-days'] as const, reportPalette: ['tenant', 'report-palette'] as const };

export function useTenantInfo() {
  return useQuery({ queryKey: tenantKeys.info, queryFn: tenantApi.info });
}

export function useMembers() {
  return useQuery({ queryKey: tenantKeys.members, queryFn: () => tenantApi.members({ size: 100 }) });
}

export function useMemberCount(): number | null {
  const { data } = useQuery({ queryKey: [...tenantKeys.members, 'count'], queryFn: () => tenantApi.members({ size: 1 }), staleTime: 5 * 60_000 });
  return data?.total ?? null;
}

export function useTenantInvitations() {
  return useQuery({ queryKey: tenantKeys.invitations, queryFn: tenantApi.invitations });
}

export function useRoles() {
  return useQuery({ queryKey: tenantKeys.roles, queryFn: tenantApi.roles, staleTime: 60_000 });
}

export function usePermissionCatalog() {
  return useQuery({ queryKey: tenantKeys.permissions, queryFn: tenantApi.permissions, staleTime: Infinity });
}

export function useJobTitles() {
  return useQuery({ queryKey: tenantKeys.jobTitles, queryFn: tenantApi.jobTitles, staleTime: 60_000 });
}

export function useDepartments() {
  return useQuery({ queryKey: tenantKeys.departments, queryFn: tenantApi.departments, staleTime: 60_000 });
}

/** Whether the organisation already defines departments (then members and projects must choose one). */
export function useDepartmentsRequired() {
  const departments = useDepartments();
  return (departments.data ?? []).length > 0;
}

export function useCatalog(kind: CatalogKind) {
  return useQuery({ queryKey: tenantKeys.catalog(kind), queryFn: () => tenantApi.catalog(kind), staleTime: 60_000 });
}

export function useWorkingDays() {
  return useQuery({ queryKey: tenantKeys.workingDays, queryFn: tenantApi.workingDays, staleTime: 5 * 60_000 });
}

export function useReportPalette() {
  return useQuery({ queryKey: tenantKeys.reportPalette, queryFn: tenantApi.reportPalette, staleTime: 5 * 60_000 });
}

/** Members' hourly rates: only for those who manage members (the server refuses everyone else). */
export function useMemberRates(enabled: boolean) {
  return useQuery({ queryKey: ['tenant', 'member-rates'], queryFn: tenantApi.memberRates, enabled });
}
